import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselEmissionLoadingStatus } from "../../../services/vesselEmissionService";
import { SlidersHorizontal } from "lucide-react";

const loadingTypes = {
  ballast: {
    name: "Ballast",
    unit: "hours",
    color: "#1a5276",
  },
  laden: {
    name: "Laden",
    unit: "hours",
    color: "#27ae60",
  },
};

const ShipLoadingState = ({ voyageId }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [loadingState, setLoadingState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(["ballast", "laden"]);

  useEffect(() => {
    const fetchLoadingState = async () => {
      if (!currentVessel?._id) {
        console.log("No vessel selected, skipping fetch");
        return;
      }

      if (!voyageId && (!startDate || !endDate)) {
        console.log("No voyage selected and missing date range, skipping fetch");
        return;
      }

      console.log("Fetching emission overview with:", {
        vesselId: currentVessel._id,
        voyageId,
        startDate: voyageId ? null : startDate,
        endDate: voyageId ? null : endDate,
      });

      setLoading(true);
      try {
        const response = await getVesselEmissionLoadingStatus(
          currentVessel._id,
          voyageId ? null : startDate,
          voyageId ? null : endDate,
          voyageId
        );

        if (response?.status === "error") {
          setError("Failed to fetch loading status data");
          setLoadingState(null);
        } else {
          setLoadingState(response || []);
          setError(null);
        }
      } catch (err) {
        setError(err.message);
        console.error("API Error:", err);
        setLoadingState(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLoadingState();
  }, [currentVessel, startDate, endDate, voyageId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const handleToggle = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const prepareChartData = () => {
    if (!loadingState || loadingState.length === 0) {
      return {
        categories: [],
        series: [],
      };
    }

    const categories = loadingState.map((item) => formatDate(item.date));

    // Filter series based on selected types
    const series = [];

    if (selectedTypes.includes("ballast")) {
      series.push({
        name: "Ballast",
        data: loadingState.map((item) => item.ballast),
        color: loadingTypes.ballast.color,
      });
    }

    if (selectedTypes.includes("laden")) {
      series.push({
        name: "Laden",
        data: loadingState.map((item) => item.laden),
        color: loadingTypes.laden.color,
      });
    }

    return {
      categories,
      series,
    };
  };

  const chartData = prepareChartData();

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 400,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: chartData.categories,
      crosshair: true,
      labels: {
        style: {
          color: "#64748b",
          fontSize: "12px",
        },
      },
      tickPositions: (function () {
        const totalDays = chartData.categories.length;

        if (totalDays <= 7) {
          // Show all days if <= 1 week
          return chartData.categories.map((_, idx) => idx);
        } else {
          // Show weekly interval starting from the first date
          return chartData.categories
            .map((_, idx) => (idx % 7 === 0 ? idx : null))
            .filter((v) => v !== null);
        }
      })(),
    },
    yAxis: {
      min: 0,
      gridLineColor: "#f1f5f9",
      title: {
        text: "Hours",
        style: { color: "#64748b" },
      },
      stackLabels: {
        enabled: false,
        style: {
          fontWeight: "700",
          color: "#1a5276",
          fontSize: "13px",
        },
      },
    },
    tooltip: {
      backgroundColor: "#ffffff",
      borderColor: "#1a5276",
      borderRadius: 8,
      shadow: true,
      style: {
        color: "#1a5276",
        fontSize: "14px",
      },
      formatter: function () {
        const date = new Date(this.key);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        });

        return `<b>${formattedDate}</b><br/>
          <span style="color:${this.series.color}">${this.series.name}</span>: 
          <b>${this.y}</b> hrs<br/>
          Total: <b>${this.point.stackTotal}</b> hrs`;
      },
    },
    plotOptions: {
      column: {
        stacking: selectedTypes.length > 1 ? "normal" : null, // Only stack if multiple types selected
        borderRadius: 12,
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.1,
        dataLabels: {
          enabled: false,
          formatter: function () {
            // Only show label if value is greater than 0
            return this.y > 0 ? this.y : null;
          }
        },
      },
    },
    series: chartData.series,
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false, // We're using our custom filter buttons instead
      itemStyle: {
        color: "#444",
        fontWeight: "500",
        fontSize: "14px",
      },
    },
  };

  if (loading) {
    return (
      <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center">
        Loading ship loading status...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center rounded-tl-2xl rounded-tr-2xl">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
          Ship Loading Status
        </h2>
      </div>

      {/* Loading State Toggles */}
      <div className="flex justify-end gap-4 px-6 mt-4">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-2">
          {Object.keys(loadingTypes).map((type) => {
            const { name, color } = loadingTypes[type];
            const isActive = selectedTypes.includes(type);

            return (
              <button
                key={type}
                onClick={() => handleToggle(type)}
                className={`relative px-3 py-2 text-sm rounded-lg transition-all duration-200 flex items-center gap-2 overflow-hidden
                    ${isActive
                    ? "text-white shadow-md"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                style={{
                  backgroundColor: isActive ? color : undefined,
                  borderColor: isActive ? color : "#e2e8f0",
                }}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="">
        {loadingState && loadingState.length > 0 && chartData.series.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            No loading status data available
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipLoadingState;